import React from 'react'
import { getItemIcon } from '../icons/index.js'
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@material-ui/core'
import {
  ExpandLess,
  ExpandMore
} from '@material-ui/icons'

const DROP_DEBUG = process.env.NODE_ENV === 'development'
const SUPPRESS_CLICK_AFTER_DROP_MS = 400

const TreeView = ({
  items,
  onItemSelected,
  onItemContextMenu = () => {},
  expand,
  isSelected,
  scrollSelectedIntoView = false,
  scrollExpandedFolderIntoView = false,
  renderCreateFolder = () => null,
  renderCustomIcons = () => null,
  canDropOnItem = () => false,
  onDropOnItem = () => {}
}) => {
  const [dropTargetId, setDropTargetId] = React.useState(null)
  const suppressClickAfterDropRef = React.useRef({
    itemId: null,
    expiresAt: 0
  })
  const selectedItemRef = React.useRef(null)
  const itemRowRefs = React.useRef(new Map())
  const itemContainerRefs = React.useRef(new Map())
  const lastScrolledSelectionRef = React.useRef('')
  const previousExpandRef = React.useRef({})
  const lastExpandedFolderIdRef = React.useRef('')
  const lastExpandedSnapshotRef = React.useRef('')

  React.useEffect(() => {
    if (!scrollSelectedIntoView) {
      return
    }

    const selectedElement = selectedItemRef.current
    if (!selectedElement) {
      lastScrolledSelectionRef.current = ''
      return
    }

    const itemId = String(selectedElement.getAttribute('data-tree-item-id') || '')
    const itemPath = String(selectedElement.getAttribute('data-tree-item-path') || '')
    const selectionKey = `${itemId}:${itemPath}`

    if (!selectionKey || selectionKey === lastScrolledSelectionRef.current) {
      return
    }

    lastScrolledSelectionRef.current = selectionKey
    const frameId = window.requestAnimationFrame(() => {
      selectedElement.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [items, expand, isSelected, scrollSelectedIntoView])

  React.useEffect(() => {
    const nextExpand = expand || {}
    const prevExpand = previousExpandRef.current || {}
    const newlyExpandedIds = Object.keys(nextExpand).filter((id) => nextExpand[id] && !prevExpand[id])
    previousExpandRef.current = nextExpand

    if (!scrollExpandedFolderIntoView) {
      return
    }

    if (newlyExpandedIds.length > 0) {
      lastExpandedFolderIdRef.current = String(newlyExpandedIds[newlyExpandedIds.length - 1])
      lastExpandedSnapshotRef.current = ''
    }

    const expandedId = String(lastExpandedFolderIdRef.current || '')
    if (!expandedId || !nextExpand[expandedId]) {
      return
    }

    const expandedTreeItem = findTreeItemById(items, expandedId)
    const childrenCount = Array.isArray(expandedTreeItem?.children) ? expandedTreeItem.children.length : 0
    const snapshot = `${expandedId}:${childrenCount}`
    if (!newlyExpandedIds.length && snapshot === lastExpandedSnapshotRef.current) {
      return
    }
    lastExpandedSnapshotRef.current = snapshot

    const rafId = window.requestAnimationFrame(() => {
      scrollExpandedItemIntoView(expandedId, itemRowRefs.current, itemContainerRefs.current)
    })
    const timeoutId = window.setTimeout(() => {
      scrollExpandedItemIntoView(expandedId, itemRowRefs.current, itemContainerRefs.current)
    }, 260)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [expand, items, scrollExpandedFolderIntoView])

  const renderItem = (item, level, index) => {
    const ItemIcon = getItemIcon(item)
    const children = Array.isArray(item.children) ? item.children : []
    const canDrop = canDropOnItem(item)
    const isDropTarget = dropTargetId === item.id
    const key = item?.id || `${item?.type || 'item'}:${item?.path || item?.name || 'unknown'}:${level}:${index}`
    const selected = isSelected(item)

    const shouldSuppressClick = () => {
      const marker = suppressClickAfterDropRef.current || {}
      return marker.itemId === item.id && Date.now() < Number(marker.expiresAt || 0)
    }

    const renderArrow = () => {
      if (Array.isArray(item.children) && item.type !== 'rootfolder') {
        if (expand[item.id]) {
          return <ExpandLess style={{ height: 16, width: 16 }} />
        } else {
          return <ExpandMore style={{ height: 16, width: 16 }} />
        }
      } else {
        return null
      }
    }

    const renderedChildren = children.length > 0
      ? (<Collapse in={expand[item.id]}><List style={{ padding: 0, marginLeft: '20px' }}>{renderItems(children, level + 1)}</List></Collapse>)
      : null

    const primary = (<div style={listItemTextStyle}>{item.name}</div>)

    return (
      <div
        key={key}
        ref={(node) => setItemRef(itemContainerRefs.current, item?.id, node)}
      >
        <Tooltip
          title={String(item?.name || '')}
          enterDelay={700}
          enterNextDelay={700}
          arrow
          placement='top-start'
          PopperProps={{
            disablePortal: true,
            modifiers: {
              offset: {
                enabled: true,
                offset: '0, -22'
              }
            }
          }}
        >
          <ListItem
            ref={(node) => {
              setItemRef(itemRowRefs.current, item?.id, node)
              if (selected) {
                selectedItemRef.current = node
              } else if (selectedItemRef.current === node) {
                selectedItemRef.current = null
              }
            }}
            data-tree-item-id={String(item?.id ?? '')}
            data-tree-item-path={String(item?.path || '')}
            data-tree-item-type={String(item?.type || '')}
            onClick={(e) => {
              if (shouldSuppressClick()) {
                e.preventDefault()
                e.stopPropagation()
                return
              }
              onItemSelected(e, item)
            }}
            onContextMenu={(e) => onItemContextMenu(e, item)}
            onDragEnter={(e) => {
              if (!canDrop) return
              e.preventDefault()
              logDrop('attachment-drop: drag enter on folder', {
                itemId: item?.id,
                itemName: item?.name,
                itemType: item?.type
              })
              if (!isDropTarget) {
                setDropTargetId(item.id)
              }
            }}
            onDragOver={(e) => {
              if (!canDrop) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
              if (!isDropTarget) {
                setDropTargetId(item.id)
              }
            }}
            onDragLeave={() => {
              if (isDropTarget) {
                setDropTargetId(null)
              }
            }}
            onDrop={(e) => {
              if (!canDrop) return
              e.preventDefault()
              e.stopPropagation()
              logDrop('attachment-drop: drop event on folder', {
                itemId: item?.id,
                itemName: item?.name,
                itemType: item?.type
              })
              suppressClickAfterDropRef.current = {
                itemId: item.id,
                expiresAt: Date.now() + SUPPRESS_CLICK_AFTER_DROP_MS
              }
              setDropTargetId(null)
              onDropOnItem(e, item)
            }}
            button
            selected={selected}
            style={Object.assign({}, listItemStyle(item), isDropTarget ? dropTargetStyle : null)}
          >
            {renderArrow()}
            <ListItemIcon style={{ minWidth: 18 }}>
              <ItemIcon style={{ height: 16 }} />
            </ListItemIcon>
            <ListItemText primary={primary} />
            {renderCreateFolder(item)}
            {renderCustomIcons(item)}
          </ListItem>
        </Tooltip>
        {renderedChildren}
      </div>
    )
  }

  const renderItems = (items, level) => items.map((item, index) => renderItem(item, level, index))

  return (
    <List style={{ padding: 0 }}>
      {renderItems(items, 0)}
    </List>
  )
}

const listItemStyle = (item) => {
  return {
    paddingLeft: item.type === 'rootfolder' ? '10px' : 0,
    paddingRight: 0,
    paddingTop: '2px',
    paddingBottom: '2px'
  }
}

const listItemTextStyle = {
  marginTop: 2,
  marginLeft: 4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '0.7rem'
}

const dropTargetStyle = {
  outline: '2px dotted #2e7d32',
  outlineOffset: '-2px',
  borderRadius: 4,
  backgroundColor: 'rgba(46, 125, 50, 0.08)'
}

function logDrop (message, payload) {
  if (DROP_DEBUG) {
    console.info(message, payload)
  }
}

function setItemRef (refMap, itemId, node) {
  if (itemId === undefined || itemId === null) {
    return
  }

  const key = String(itemId)
  if (node) {
    refMap.set(key, node)
  } else {
    refMap.delete(key)
  }
}

function scrollExpandedItemIntoView (itemId, itemRowRefs, itemContainerRefs) {
  const rowElement = itemRowRefs.get(itemId)
  const itemContainer = itemContainerRefs.get(itemId)
  if (!rowElement || !itemContainer) {
    return
  }

  const itemType = String(rowElement.getAttribute('data-tree-item-type') || '')
  if (itemType !== 'folder' && itemType !== 'rootfolder') {
    return
  }

  const scrollContainer = getScrollContainer(rowElement)
  if (!scrollContainer) {
    return
  }

  const containerRect = scrollContainer.getBoundingClientRect()
  const rowRect = rowElement.getBoundingClientRect()
  const expandedRect = itemContainer.getBoundingClientRect()

  const viewportHeight = scrollContainer.clientHeight
  if (viewportHeight <= 0) {
    return
  }

  const rowTop = rowRect.top - containerRect.top + scrollContainer.scrollTop
  const expandedBottom = expandedRect.bottom - containerRect.top + scrollContainer.scrollTop
  const expandedHeight = Math.max(0, expandedBottom - rowTop)

  let targetScrollTop = scrollContainer.scrollTop
  if (expandedHeight <= viewportHeight) {
    if (rowTop < targetScrollTop) {
      targetScrollTop = rowTop
    } else if (expandedBottom > targetScrollTop + viewportHeight) {
      targetScrollTop = expandedBottom - viewportHeight
    }
  } else {
    targetScrollTop = rowTop
  }

  if (Math.abs(targetScrollTop - scrollContainer.scrollTop) > 1) {
    scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
  }
}

function getScrollContainer (element) {
  if (!element) {
    return null
  }

  const fileView = document.getElementById('file-view-wrapper')
  if (fileView && fileView.contains(element)) {
    return fileView
  }

  return findScrollableAncestor(element)
}

function findScrollableAncestor (element) {
  let current = element?.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = String(style.overflowY || '')
    const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight
    if (isScrollable) {
      return current
    }
    current = current.parentElement
  }

  return null
}

function findTreeItemById (items, id) {
  const targetId = String(id || '')
  if (!targetId || !Array.isArray(items)) {
    return null
  }

  const queue = items.slice()
  while (queue.length > 0) {
    const current = queue.shift()
    if (String(current?.id || '') === targetId) {
      return current
    }
    const children = Array.isArray(current?.children) ? current.children : []
    children.forEach((child) => queue.push(child))
  }

  return null
}

export default TreeView
